import React from "react";
import { Box, Container, Typography, Paper, Chip, useTheme } from "@mui/material";

/**
 * Money Map — a vertical focus chart that sorts every effort by the kind of
 * money it makes: Consistent (keeps the lights on), Quick (near-term cash),
 * Big (the upside), plus the non-money anchor that the rest exists for.
 *
 * Presentational only; no data fetching. Styled from the app theme so it reads
 * as part of Focus: Finance (teal TPC brand) with per-category accent colors.
 */

type Node = {
  title: string;
  meta: string;
  desc: string;
  tag: string;
};

type Band = {
  key: string;
  heading: string;
  sub: string;
  color: string;
  flow?: React.ReactNode;
  nodes: Node[];
};

const MoneyMap: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Per-category accents. Consistent uses the brand teal; the rest are chosen
  // to sit beside it and stay legible in both themes.
  const C = {
    consistent: theme.palette.primary.main,
    quick: isDark ? "#e5b74f" : "#c68a1d",
    big: isDark ? "#b79be6" : "#7a5bb0",
    life: isDark ? "#7fb0e0" : "#3a6ea5",
    maint: isDark ? "#b0ab9c" : "#8a8578",
  };

  const bands: Band[] = [
    {
      key: "consistent",
      heading: "Consistent Money",
      sub: "Recurring · keeps the lights on",
      color: C.consistent,
      nodes: [
        {
          title: "Serve SEO & Ads clients",
          meta: "MONTHLY RETAINERS",
          desc: "The dependable base that pays the bills every month — and every repetitive task here is a spec for the platform you're building.",
          tag: "The floor",
        },
      ],
    },
    {
      key: "quick",
      heading: "Quick Money",
      sub: "Near-term cash · clears the deck",
      color: C.quick,
      nodes: [
        {
          title: "Finish 3 client websites",
          meta: "SHIP TO GET PAID",
          desc: "Three open builds sitting on unbilled revenue. Closing them frees cash and attention.",
          tag: "Fastest cash",
        },
        {
          title: "Full-time job applications",
          meta: "~2 HRS / WEEK",
          desc: "A low-burn hedge toward a steady paycheck. Capped at two hours so it never eats the build.",
          tag: "Income hedge",
        },
      ],
    },
    {
      key: "big",
      heading: "Big Money",
      sub: "The upside · changes the game",
      color: C.big,
      flow: (
        <>
          Consistent work feeds <b>the platform</b> — the platform feeds{" "}
          <b>the rebrand</b>.
        </>
      ),
      nodes: [
        {
          title: "Build the SEO & Ads platform",
          meta: "AUTOMATE THE SERVICE",
          desc: "Turn the client work you do by hand into a system that runs itself. The pivot from trading hours to owning an asset.",
          tag: "Core bet",
        },
        {
          title: "Sell the platform with Lane",
          meta: "WHEN IT'S DONE",
          desc: "Hand the finished product to your sales partner and open it to new customers. Revenue that doesn't depend on your hours.",
          tag: "Scales with Lane",
        },
        {
          title: "Rebrand TPC as AI-factory builders",
          meta: "NEW NORTH STAR",
          desc: "Not websites, apps, SEO or PPC — TPC builds AI factories that autonomously automate and scale businesses through systems and audit trails. The platform is Exhibit A.",
          tag: "The big story",
        },
      ],
    },
    {
      key: "life",
      heading: "Not About Money",
      sub: "The reason for all of it",
      color: C.life,
      nodes: [
        {
          title: "Kids, wife, dates & rest",
          meta: "NON-NEGOTIABLE",
          desc: "The reason for the work and the fuel that keeps the engine running. Protect this time first, then schedule the money around it.",
          tag: "Protect first",
        },
        {
          title: "Property projects & car repairs",
          meta: "AS NEEDED",
          desc: "Maintenance of the home base. Batch it so it doesn't scatter the week.",
          tag: "Keep it batched",
        },
      ],
    },
  ];

  // The last band ("life") uses a secondary maintenance accent on its 2nd node.
  const nodeColor = (bandKey: string, idx: number) =>
    bandKey === "life" && idx === 1 ? C.maint : undefined;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="overline"
          sx={{ color: C.consistent, fontWeight: 700, letterSpacing: "0.18em" }}
        >
          Focus · Money Map
        </Typography>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, lineHeight: 1.05, mt: 0.5, mb: 1 }}
        >
          Three kinds of money.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: "52ch" }}>
          Every effort sorted by the kind of money it makes — the consistent
          money that keeps the lights on, the quick money that clears the deck,
          and the big money that changes the game. Life sits at the base: the
          reason for all of it.
        </Typography>
      </Box>

      <Box sx={{ position: "relative" }}>
        {/* the spine */}
        <Box
          sx={{
            position: "absolute",
            left: 15,
            top: 8,
            bottom: 8,
            width: "2px",
            bgcolor: "divider",
          }}
        />

        {bands.map((band) => (
          <Box key={band.key} sx={{ position: "relative" }}>
            {/* band header */}
            <Box sx={{ position: "relative", pl: 5.5, mt: 4, mb: 1.5 }}>
              <Box
                sx={{
                  position: "absolute",
                  left: 9,
                  top: 6,
                  width: 14,
                  height: 14,
                  borderRadius: "4px",
                  bgcolor: band.color,
                  boxShadow: `0 0 0 4px ${theme.palette.background.default}`,
                }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: band.color, lineHeight: 1.1 }}
              >
                {band.heading}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  color: "text.secondary",
                  fontWeight: 600,
                }}
              >
                {band.sub}
              </Typography>
            </Box>

            {band.flow && (
              <Typography
                variant="body2"
                sx={{
                  pl: 5.5,
                  mb: 1.5,
                  fontStyle: "italic",
                  color: "text.secondary",
                  "& b": { color: band.color, fontStyle: "normal" },
                }}
              >
                {band.flow}
              </Typography>
            )}

            {band.nodes.map((node, idx) => {
              const accent = nodeColor(band.key, idx) ?? band.color;
              return (
                <Box
                  key={node.title}
                  sx={{ position: "relative", pl: 5.5, mb: 1.75 }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 8,
                      top: 22,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      bgcolor: "background.paper",
                      border: `3px solid ${accent}`,
                      boxShadow: `0 0 0 4px ${theme.palette.background.default}`,
                    }}
                  />
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderLeft: `4px solid ${accent}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {node.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: accent,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {node.meta}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      {node.desc}
                    </Typography>
                    <Chip
                      label={node.tag}
                      size="small"
                      sx={{
                        mt: 1.25,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        color: accent,
                        bgcolor: `${accent}22`,
                      }}
                    />
                  </Paper>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 2,
          borderRadius: 2,
          bgcolor: "action.hover",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <b>Reading it:</b> three money buckets, top to bottom. Consistent keeps
          you alive, Quick buys runway, Big is where the leverage lives — and the
          arrows show how one funds the next. When a day feels scattered, do the
          highest-leverage Big-Money item you can move today, on top of the
          consistent base.
        </Typography>
      </Paper>
    </Container>
  );
};

export default MoneyMap;
